import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import qrcode from "qrcode-generator";
import { trpc } from "@/lib/trpc";
import { sairDoAdmin } from "@/lib/login-do-admin";

const botao = "w-full rounded-lg bg-red-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-50";

function qrSvg(texto: string) {
  const qr = qrcode(0, "M");
  qr.addData(texto);
  qr.make();
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qr.createSvgTag({ cellSize: 6, margin: 4, scalable: true }))}`;
}

async function entrarDeNovo() {
  try {
    await sairDoAdmin();
  } finally {
    window.location.href = "/login";
  }
}

/** Admin sem segundo fator: cadastra o app autenticador antes de usar o painel. */
export function ConfigurarMfa() {
  const [codigo, setCodigo] = useState("");
  const iniciar = trpc.mfaDoAdmin.iniciar.useMutation();
  const ativar = trpc.mfaDoAdmin.ativar.useMutation();

  if (ativar.data) {
    return (
      <Moldura titulo="Guarde os códigos de recuperação">
        <p className="text-sm text-admin-200/80">
          Cada código entra uma vez, no lugar do aplicativo, se você perder o celular. Eles não aparecem de novo.
        </p>
        <ul className="grid grid-cols-2 gap-2 rounded-lg border border-admin-800 bg-admin-900 p-3 font-mono text-sm text-white">
          {ativar.data.codigosDeRecuperacao.map((c) => <li key={c}>{c}</li>)}
        </ul>
        <button type="button" className={botao} onClick={entrarDeNovo}>Já guardei — entrar com o código</button>
      </Moldura>
    );
  }

  return (
    <Moldura titulo="Ative a verificação em duas etapas">
      <p className="text-sm text-admin-200/80">
        O painel admin exige um código do aplicativo autenticador (Google Authenticator, Authy, 1Password) além da senha.
      </p>
      {!iniciar.data ? (
        <button type="button" className={botao} disabled={iniciar.isPending} onClick={() => iniciar.mutate()}>
          {iniciar.isPending ? "Gerando..." : "Gerar código QR"}
        </button>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            ativar.mutate({ codigo });
          }}
        >
          <img src={qrSvg(iniciar.data.uri)} alt="QR para o aplicativo autenticador" className="mx-auto h-48 w-48 rounded-lg bg-white" />
          <p className="break-all text-center font-mono text-xs text-admin-200/70">{iniciar.data.segredo}</p>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            placeholder="Código de 6 dígitos"
            required
            className="w-full rounded-lg border border-admin-800 bg-admin-900 px-3 py-2 text-sm text-white placeholder-admin-200/30 focus:border-red-500 focus:outline-none"
          />
          {ativar.error && <p className="text-sm text-red-300">{ativar.error.message}</p>}
          <button type="submit" className={botao} disabled={ativar.isPending}>
            {ativar.isPending ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Ativar"}
          </button>
        </form>
      )}
    </Moldura>
  );
}

/** Admin com segundo fator, mas a sessão veio do login comum: entra de novo pelo login do admin. */
export function EntrarComCodigo() {
  return (
    <Moldura titulo="Entre com o código">
      <p className="text-sm text-admin-200/80">
        Esta sessão veio do login comum. O painel admin pede o código do aplicativo autenticador.
      </p>
      <button type="button" className={botao} onClick={entrarDeNovo}>Entrar pelo login do admin</button>
    </Moldura>
  );
}

function Moldura({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-admin-900 p-4">
      <div className="w-full max-w-md space-y-4 rounded-2xl border border-admin-800 bg-admin-950 p-6 shadow-xl">
        <div className="flex items-center gap-2 text-white">
          <KeyRound className="h-5 w-5 text-red-400" />
          <h1 className="text-lg font-semibold">{titulo}</h1>
        </div>
        {children}
      </div>
    </div>
  );
}
